import Image from 'next/image'
import React, { Fragment, memo, useState } from 'react'
import { CompatibleLink } from 'src/components/CompatibleLink/CompatibleLink'
import { INITIAL_ITEM_CNT, PRACTICE_AREAS_URL } from 'src/config/config'
import { IGetTopicsForHeaderQuery, useGetTopicsForHeaderQuery } from 'src/graphql/generated/hooks'
import { FeaturesType } from 'src/types'

import CartSubContent from '../Cart/CartSubContent'
import { ThemeOnly } from '../ThemeOnly'

interface SpanDataListProps {
  isProductTypes?: boolean
  isPracticeAreas?: boolean
  features: FeaturesType[]
  type: string
}

export const SpanDataList: React.FC<SpanDataListProps> = memo(
  ({ isProductTypes, isPracticeAreas, features = [], type = '' }) => {
    const [active, setActive] = useState(false)
    const handleclick = () => {
      setActive(!active)
    }

    let topics: IGetTopicsForHeaderQuery | undefined = {}

    if (isProductTypes) {
      const { data } = useGetTopicsForHeaderQuery({ fetchPolicy: 'cache-first' })
      topics = data
    }

    const findTopic = (subTopicName: string) => {
      const find = topics?.topics?.data.filter((item) => {
        const subTopics = item?.attributes?.subTopics?.data
        return subTopics?.some((subTopic) => subTopic?.attributes?.name === subTopicName)
      })

      return find && find.length > 0
        ? `/topics/${find[0]?.attributes?.slug}/${
            find[0].attributes?.subTopics?.data.filter((item) => item.attributes?.name === subTopicName)[0]?.attributes
              ?.slug
          }`
        : ''
    }

    return (
      <div>
        {type === 'normal' && (
          <div className="flex flex-wrap" style={{ overflow: 'hidden' }}>
            {features.map((feature: FeaturesType, index: number) => {
              return (
                <CartSubContent key={index} className="mr-[0.125rem] mt-[0.125rem]">
                  {feature.name}
                  {index !== features.length - 1 ? ',' : ''}
                </CartSubContent>
              )
            })}
          </div>
        )}
        {type === 'more' && (
          <div className="flex flex-wrap items-center">
            {features.map((item: FeaturesType, index: number) => {
              if (index < INITIAL_ITEM_CNT) {
                return (
                  <CartSubContent key={index} className="mr-[0.125rem] mt-[0.125rem]">
                    {item.name}
                    {index !== features.length - 1 ? ',' : ''}
                  </CartSubContent>
                )
              } else {
                if (active) {
                  return (
                    <CartSubContent key={index} className="mr-[0.125rem] mt-[0.125rem]">
                      {item.name}
                      {index !== features.length - 1 ? ',' : ''}
                    </CartSubContent>
                  )
                } else {
                  return <div key={index}></div>
                }
              }
            })}
            {!active && features.length > INITIAL_ITEM_CNT && (
              <div
                className="h-[1.25rem] w-full text-textdark text-[16px] font-primary font-bold mb-[0.125rem] underline cursor-pointer"
                onClick={handleclick}
              >
                +{features.length - INITIAL_ITEM_CNT} {'more'}
              </div>
            )}
          </div>
        )}
        {type !== 'normal' && type !== 'more' && (
          <div className="flex flex-wrap items-center">
            {features.map((item: FeaturesType, index: number) => {
              if (index < INITIAL_ITEM_CNT) {
                return (
                  <Fragment>
                    {isProductTypes ? (
                      <CompatibleLink
                        href={findTopic(item.name)}
                        key={index}
                        className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px]"
                      >
                        {item.icon && (
                          <div className="mr-[9px] h-[19px]">
                            <Image src={item.icon} className="w-full h-full" alt="" />
                          </div>
                        )}
                        {item.name}
                      </CompatibleLink>
                    ) : isPracticeAreas === true &&
                      !!item?.id &&
                      PRACTICE_AREAS_URL !== 'false' &&
                      !!PRACTICE_AREAS_URL ? (
                      <ThemeOnly theme="legaltech">
                        <a
                          href={`/search/?productType=&practiceAreas=${item.id}`}
                          rel="noopener noreferrer"
                          className="cursor-pointer compare-practice-areas"
                        >
                          <div
                            key={index}
                            className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px]"
                          >
                            {item.icon && (
                              <div className="mr-[9px] h-[19px]">
                                <Image src={item.icon} className="w-full h-full" alt="" />
                              </div>
                            )}
                            {item.name}
                          </div>
                        </a>
                      </ThemeOnly>
                    ) : (
                      <div
                        key={index}
                        className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px]"
                      >
                        {item.icon && (
                          <div className="mr-[9px] h-[19px]">
                            <Image src={item.icon} className="w-full h-full" alt="" />
                          </div>
                        )}
                        {item.name}
                      </div>
                    )}
                  </Fragment>
                )
              } else {
                if (
                  active &&
                  isPracticeAreas === true &&
                  !!item?.id &&
                  PRACTICE_AREAS_URL !== 'false' &&
                  !!PRACTICE_AREAS_URL
                ) {
                  return (
                    <>
                      <ThemeOnly theme="legaltech">
                        <a
                          href={`/search/?productType=&practiceAreas=${item.id}`}
                          rel="noopener noreferrer"
                          className="cursor-pointer compare-practice-areas"
                        >
                          <div
                            key={index}
                            className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px] min-h-[30px] w-fit"
                          >
                            {item.name}
                          </div>
                        </a>
                      </ThemeOnly>
                      <ThemeOnly theme="ilta">
                        <div
                          key={index}
                          className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px] min-h-[30px] w-fit"
                        >
                          {item.name}
                        </div>
                      </ThemeOnly>
                    </>
                  )
                } else if (active) {
                  return (
                    <div
                      key={index}
                      className="flex flex-row bg-clouds text-textdark text-[16px] font-primary font-medium items-center text-center rounded-[10px] mt-1.5 mr-[6px] px-[20px] pt-[8.86px] pb-[8.14px] min-h-[30px] w-fit"
                    >
                      {item.name}
                    </div>
                  )
                } else {
                  return <div key={index}></div>
                }
              }
            })}
            {!active && features.length > INITIAL_ITEM_CNT && (
              <div
                className="h-[1.25rem] text-textdark text-[16px] font-primary font-medium mt-1.5 underline cursor-pointer"
                onClick={handleclick}
              >
                +{features.length - INITIAL_ITEM_CNT} {'more'}
              </div>
            )}
          </div>
        )}
      </div>
    )
  },
)
